"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getStudyPlanSyncPreview } from "@/lib/server-actions/studyPlanActions";
import { STUDY_PLAN_LABELS, type StudyPlanId } from "@/lib/study-plan";
import type { StudyPlanSyncCandidate } from "@/lib/study-plan-sync";
import { codeLangLabel } from "@/app/my/schedule/schedule-shared";

type Preview = {
  loading: boolean;
  candidates: StudyPlanSyncCandidate[];
  error?: string;
};
export default function StudyPlanSyncDialog({
  open,
  onOpenChange,
  planIds,
  examSessionId,
  onSync,
  startedPlanId,
}: {
  startedPlanId?: StudyPlanId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planIds: StudyPlanId[];
  examSessionId: string;
  onSync: (planIds: StudyPlanId[]) => Promise<boolean>;
}) {
  const [selected, setSelected] = useState<StudyPlanId[]>([]);
  const [preview, setPreview] = useState<Preview>({
    loading: false,
    candidates: [],
  });
  const [saving, setSaving] = useState(false);
  const selection = selected.filter((id) => planIds.includes(id));
  const selectionKey = selection.join(",");
  useEffect(() => {
    if (!open || !selectionKey || selectionKey.split(",").length < 2) return;
    let cancelled = false;
    void getStudyPlanSyncPreview(
      selectionKey.split(",") as StudyPlanId[],
      examSessionId,
    )
      .then((result) => {
        if (!cancelled)
          setPreview({
            loading: false,
            candidates: result.candidates ?? [],
            error: result.success ? undefined : result.error,
          });
      })
      .catch(() => {
        if (!cancelled)
          setPreview({
            loading: false,
            candidates: [],
            error: "완료 기록을 확인하지 못했어요. 다시 선택해주세요.",
          });
      });
    return () => {
      cancelled = true;
    };
  }, [open, selectionKey, examSessionId]);

  const changeSelection = (id: StudyPlanId, checked: boolean) => {
    setSelected((prev) =>
      checked ? [...prev, id] : prev.filter((item) => item !== id),
    );
    setPreview({ loading: true, candidates: [] });
  };
  const close = (next: boolean) => {
    if (saving) return;
    onOpenChange(next);
    if (!next) {
      setSelected([]);
      setPreview({ loading: false, candidates: [] });
    }
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>계획 간 완료 일정 동기화</DialogTitle>
          <DialogDescription>
            {startedPlanId ? `${STUDY_PLAN_LABELS[startedPlanId]} 학습을 시작했어요. 다른 계획과 겹치는 완료 일정을 지금 동기화할까요? ` : ""}
            동기화할 계획을 두 개 이상 선택하세요. 선택한 계획의 완료 기록을
            서로 반영해요. 같은 페이지·언어의 첫 회독만 완료하며 회독 수는
            늘리지 않아요.
          </DialogDescription>
        </DialogHeader>
        <fieldset className="space-y-2" disabled={saving}>
          <legend className="mb-2 text-sm font-medium">동기화할 계획</legend>
          {planIds.map((id) => (
            <label
              key={id}
              className="flex cursor-pointer items-center gap-3 rounded-lg border border-border p-3 text-sm hover:bg-muted"
            >
              <input
                type="checkbox"
                className="h-4 w-4 accent-current"
                checked={selection.includes(id)}
                onChange={(event) => changeSelection(id, event.target.checked)}
              />
              <span>{STUDY_PLAN_LABELS[id]}</span>
            </label>
          ))}
        </fieldset>
        <div className="rounded-lg bg-muted p-3 text-sm" aria-live="polite">
          {selection.length < 2 ? (
            "계획을 두 개 이상 선택하면 반영할 항목을 보여드려요."
          ) : preview.loading ? (
            "완료 기록 확인 중…"
          ) : preview.error ? (
            <p role="alert">{preview.error}</p>
          ) : !preview.candidates.length ? (
            "선택한 계획 사이에 새로 반영할 완료 기록이 없어요."
          ) : (
            <>
              <p className="font-medium">
                총 {preview.candidates.length}개 항목을 완료로 반영해요.
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                이미 완료했거나 건너뛴 항목과 기존 회독 수는 유지해요. 완료 날짜
                자동 이동 설정에 따라 원래 완료일로 옮겨요.
              </p>
            </>
          )}
        </div>
        {selection.length >= 2 &&
          !preview.loading &&
          preview.candidates.length > 0 && (
            <div className="max-h-[30dvh] space-y-3 overflow-y-auto overscroll-contain">
              {selection.map((id) => {
                const candidates = preview.candidates.filter(
                  (item) => item.planId === id,
                );
                return (
                  <div key={id}>
                    <h3 className="text-sm font-medium">
                      {STUDY_PLAN_LABELS[id]} · {candidates.length}개
                    </h3>
                    <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
                      {candidates.map((item) => (
                        <li key={item.taskKey} className="break-words">
                          {item.title}
                          {item.codeLang
                            ? ` · ${codeLangLabel(item.codeLang)}`
                            : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={saving}
            onClick={() => close(false)}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={
              saving ||
              selection.length < 2 ||
              preview.loading ||
              !!preview.error ||
              !preview.candidates.length
            }
            onClick={async () => {
              setSaving(true);
              try {
                if (await onSync(selection)) {
                  onOpenChange(false);
                  setSelected([]);
                  setPreview({ loading: false, candidates: [] });
                }
              } finally {
                setSaving(false);
              }
            }}
          >
            {saving
              ? "동기화 중…"
              : `${preview.candidates.length}개 항목 동기화`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
